import { TextField, Select, MenuItem } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import MyModal from 'components/MyModal';
import Spin from 'components/Spin';
import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import accessControlService from 'v2/services/access-control';

import type { User } from 'v2/interfaces/access-control';

type Props = {
  current?: User;
  onComplete: () => void;
  onCancel: () => void;
};

export default function FormUser({ current, onComplete, onCancel }: Props) {
  const [form, setForm] = useState({
    name: current?.name || '',
    email: current?.email || '',
    group: current?.group_id || 'select',
  });
  const [isLoadingGroups] = useObservable(() => accessControlService.isLoadingGroups$, []);
  const [groups] = useObservable(() => accessControlService.getGroups(), []);
  const [isSaving, setIsSaving] = useState(false);
  const [saveUser] = useCallbackObservable((data) => accessControlService.saveUser(data), []);
  const [updateUser] = useCallbackObservable((data) => accessControlService.updateUser(data), []);

  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const groupInputRef = useRef<HTMLInputElement>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setForm((old) => ({
      ...old,
      [name]: value,
    }));
  }

  const isEditing = !!current;

  function handleSubmit() {
    setIsSaving(true);
    if (!form.name) {
      nameInputRef.current.focus();
      toast.error('O nome do usuário é obrigatório.');
      setIsSaving(false);
      return;
    }
    const isEmail = /\S+@\S+\.\S+/.test(form.email);
    if (!isEmail) {
      emailInputRef.current.focus();
      toast.error('O email do usuário é inválido.');
      setIsSaving(false);
      return;
    }
    if (!form.group || form.group === 'select') {
      groupInputRef.current.focus();
      toast.error('Selecione um grupo.');
      setIsSaving(false);
      return;
    }
    if (isEditing) {
      toast.success('Sucesso, usuário atualizado!');
      updateUser({
        id: current.id,
        ...form,
      });
    } else {
      toast.success('Sucesso, usuário criado!');
      saveUser(form);
    }
    onComplete();
  }

  const groupOptions = useMemo(() => {
    return (groups || []).map((group) => ({ value: group.id, label: group.name }));
  }, [groups]);

  return (
    <MyModal
      hasActive
      title={isEditing ? 'Atualizar usuário' : 'Novo usuário'}
      subTitle={isEditing ? 'Atualize o grupo desse usuário' : 'Adicionar usuários aos grupos.'}
      closeModal={onCancel}
      textButtonOk={isEditing ? 'Atualizar' : 'Adicionar'}
      hasButtons
      handleClickOk={handleSubmit}
      loading={isSaving || isLoadingGroups}
    >
      {isLoadingGroups && <Spin />}

      {!isLoadingGroups && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Email*'
                name='email'
                value={form.email}
                disabled={isSaving || isEditing}
                helperText={isEditing ? 'Não é possível editar' : ''}
                onChange={handleChange}
                inputRef={emailInputRef}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Nome*'
                name='name'
                value={form.name}
                disabled={isSaving || isEditing}
                helperText={isEditing ? 'Não é possível editar' : ''}
                onChange={handleChange}
                inputRef={nameInputRef}
                fullWidth
              />
            </Grid>
          </Grid>

          <Select
            label='Grupo'
            name='group'
            value={form.group}
            disabled={isSaving}
            onChange={handleChange}
            fullWidth
            style={{ marginTop: 16 }}
            inputRef={groupInputRef}
          >
            <MenuItem value='select' disabled>
              Selecione o grupo
            </MenuItem>
            {groupOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </>
      )}
    </MyModal>
  );
}
